/**
 * Table-related utility functions
 */

/**
 * Get nested value from object by key path
 */
export const getByKey = (row, key) => {
  if (!row || !key) return undefined;
  
  // Handle nested keys like "Project__r.Name"
  if (key.includes('.')) {
    const parts = key.split('.');
    let value = row;
    for (const part of parts) {
      if (value === null || value === undefined) return undefined;
      value = value[part];
    }
    return value;
  }
  
  return row[key];
};

/**
 * Get unique values from an array of objects by key
 */
export const getUniqueValues = (data, key, labelMap = {}) => {
  const values = [...new Set(data.map(item => getByKey(item, key)))].filter(Boolean);
  
  // If labelMap is provided, return objects with value and label
  if (Object.keys(labelMap).length > 0) {
    return values
      .map(value => ({
        value,
        label: labelMap[value] || value
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }
  
  // Otherwise return simple array
  return values.sort();
};

/**
 * Sort array by key
 */
export const sortByKey = (array, key, direction = 'asc') => {
  return [...array].sort((a, b) => {
    let aVal = getByKey(a, key);
    let bVal = getByKey(b, key);
    
    // Handle null/undefined
    if (aVal === null || aVal === undefined) return 1;
    if (bVal === null || bVal === undefined) return -1;
    
    // Handle numeric values
    if (typeof aVal === 'number' || !isNaN(parseFloat(aVal))) {
      aVal = parseFloat(aVal) || 0;
      bVal = parseFloat(bVal) || 0;
    } else if (typeof aVal === 'string') {
      // Handle string values (case-insensitive)
      aVal = aVal.toLowerCase();
      bVal = bVal.toString().toLowerCase();
    }
    
    if (direction === 'asc') {
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
    }
    return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
  });
};

/**
 * Filter array by search term across multiple keys
 */
export const filterBySearchTerm = (array, searchTerm, keys) => {
  if (!searchTerm) return array;
  
  const term = searchTerm.toLowerCase();
  return array.filter((item) =>
    keys.some((key) => {
      const value = getByKey(item, key);
      return value?.toString().toLowerCase().includes(term);
    })
  );
};

/**
 * Apply column filters to data
 */
export const applyColumnFilters = (data, filterValues) => {
  return data.filter(row => {
    return Object.entries(filterValues).every(([key, values]) => {
      if (!values || values.length === 0) return true;
      const cellValue = getByKey(row, key);
      return values.includes(cellValue);
    });
  });
};

/**
 * Apply range filter to data
 */
export const applyRangeFilter = (data, key, range) => {
  if (!range || (range.min === null && range.max === null)) return data;
  
  return data.filter(row => {
    const value = parseFloat(getByKey(row, key)) || 0;
    if (range.min !== null && value < range.min) return false;
    if (range.max !== null && value > range.max) return false;
    return true;
  });
};

/**
 * Apply date range filter to data
 */
export const applyDateRangeFilter = (data, key, range) => {
  if (!range || (!range.start && !range.end)) return data;
  
  return data.filter(row => {
    const dateValue = getByKey(row, key);
    if (!dateValue) return false;
    
    const date = new Date(dateValue);
    if (range.start && new Date(range.start) > date) return false;
    if (range.end && new Date(range.end) < date) return false;
    return true;
  });
};

